import React, { useState, useEffect, useRef } from 'react';
import { FiSearch, FiFolder, FiUsers, FiBell, FiCreditCard, FiZap, FiBook, FiChevronRight, FiExternalLink, FiMenu, FiX } from 'react-icons/fi';
import './Docs.css';

const NAV = [
  {
    id: 'introduction',
    label: 'Introduction',
    icon: <FiBook />,
    sections: [
      { id: 'what-is-mnevia', label: 'What is Mnevia?' },
      { id: 'key-concepts', label: 'Key Concepts' },
    ],
  },
  {
    id: 'getting-started',
    label: 'Getting Started',
    icon: <FiZap />,
    sections: [
      { id: 'create-account', label: 'Create an Account' },
      { id: 'choose-role', label: 'Choose Your Role' },
      { id: 'pro-trial', label: '14-Day Pro Trial' },
    ],
  },
  {
    id: 'search',
    label: 'Search & Discovery',
    icon: <FiSearch />,
    sections: [
      { id: 'basic-search', label: 'Basic Search' },
      { id: 'search-modes', label: 'Search Modes' },
      { id: 'filters', label: 'Filters & Sorting' },
      { id: 'citation-tracing', label: 'Citation Tracing' },
      { id: 'research-monitors', label: 'Research Monitors' },
    ],
  },
  {
    id: 'collections',
    label: 'Collections',
    icon: <FiFolder />,
    sections: [
      { id: 'create-collection', label: 'Create a Collection' },
      { id: 'save-papers', label: 'Save Papers' },
      { id: 'upload-pdf', label: 'Upload PDFs' },
      { id: 'share-collection', label: 'Share with Team' },
      { id: 'ai-chat', label: 'AI Chat' },
      { id: 'lit-review', label: 'Literature Review' },
    ],
  },
  {
    id: 'teams',
    label: 'Teams',
    icon: <FiUsers />,
    sections: [
      { id: 'create-team', label: 'Create a Team' },
      { id: 'invite-members', label: 'Invite Members' },
      { id: 'team-roles', label: 'Roles & Permissions' },
      { id: 'weekly-digest', label: 'Weekly Digest' },
    ],
  },
  {
    id: 'monitors',
    label: 'Research Monitors',
    icon: <FiBell />,
    sections: [
      { id: 'monitor-setup', label: 'Set Up a Monitor' },
      { id: 'monitor-alerts', label: 'Email Alerts' },
    ],
  },
  {
    id: 'billing',
    label: 'Plans & Billing',
    icon: <FiCreditCard />,
    sections: [
      { id: 'plans-overview', label: 'Plans Overview' },
      { id: 'upgrade', label: 'Upgrade or Switch Plans' },
      { id: 'enterprise', label: 'Enterprise' },
    ],
  },
];

function DocSection({ id, title, children }) {
  return (
    <section id={id} className="docs-section">
      <h2 className="docs-section-title">{title}</h2>
      {children}
    </section>
  );
}

function DocSubSection({ id, title, children }) {
  return (
    <div id={id} className="docs-subsection">
      <h3 className="docs-subsection-title">{title}</h3>
      {children}
    </div>
  );
}

function Callout({ type = 'info', children }) {
  const icons = { info: 'ℹ️', tip: '💡', warning: '⚠️', note: '📝' };
  return (
    <div className={`docs-callout docs-callout-${type}`}>
      <span className="docs-callout-icon">{icons[type]}</span>
      <div>{children}</div>
    </div>
  );
}

function StepList({ steps }) {
  return (
    <ol className="docs-step-list">
      {steps.map((step, i) => (
        <li key={i} className="docs-step">
          <span className="docs-step-number">{i + 1}</span>
          <div className="docs-step-content">{step}</div>
        </li>
      ))}
    </ol>
  );
}

function FeatureTable({ rows }) {
  return (
    <div className="docs-table-wrap">
      <table className="docs-table">
        <thead>
          <tr>
            {rows[0].map((h, i) => <th key={i}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.slice(1).map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => <td key={j}>{cell}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Docs = ({ onNavigate }) => {
  const [activeId, setActiveId] = useState('what-is-mnevia');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const allIds = NAV.flatMap(g => g.sections.map(s => s.id));
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    allIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
    setSidebarOpen(false);
  };

  return (
    <div className="docs-root">
      {/* Mobile sidebar toggle */}
      <button className="docs-mobile-toggle" onClick={() => setSidebarOpen(o => !o)}>
        {sidebarOpen ? <FiX /> : <FiMenu />} Docs Menu
      </button>

      {/* Sidebar */}
      <aside className={`docs-sidebar${sidebarOpen ? ' docs-sidebar-open' : ''}`}>
        <div className="docs-sidebar-header">
          <span className="docs-sidebar-logo">🔬</span>
          <span className="docs-sidebar-title">Mnevia Docs</span>
        </div>
        <nav className="docs-nav">
          {NAV.map(group => (
            <div key={group.id} className="docs-nav-group">
              <div className="docs-nav-group-label">
                {group.icon} {group.label}
              </div>
              {group.sections.map(sec => (
                <button
                  key={sec.id}
                  className={`docs-nav-item${activeId === sec.id ? ' active' : ''}`}
                  onClick={() => scrollTo(sec.id)}
                >
                  <FiChevronRight className="docs-nav-chevron" />
                  {sec.label}
                </button>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      {/* Main content */}
      <main className="docs-main" ref={contentRef}>
        <div className="docs-content">

          {/* ── INTRODUCTION ── */}
          <DocSection id="introduction" title="Introduction">
            <DocSubSection id="what-is-mnevia" title="What is Mnevia?">
              <p>
                Mnevia is a research intelligence platform that helps researchers, lab managers,
                and academic teams discover, organise, and analyse scientific literature. It
                combines access to millions of scholarly works (via OpenAlex) with AI-powered
                tools, team collaboration features, and automated research monitoring.
              </p>
              <p>Whether you are a Principal Investigator managing a lab or an individual researcher
                conducting a literature review, Mnevia provides the tools you need in one place:</p>
              <ul className="docs-list">
                <li><strong>Search</strong> millions of papers by keyword, author, institution, topic, or citation.</li>
                <li><strong>Organise</strong> papers into collections and annotate them with notes and tasks.</li>
                <li><strong>Collaborate</strong> with your team by sharing collections and receiving weekly activity digests.</li>
                <li><strong>Automate</strong> literature monitoring to get email alerts when new papers match your interests.</li>
                <li><strong>Analyse</strong> collections with AI chat and generate structured literature reviews instantly.</li>
              </ul>
            </DocSubSection>

            <DocSubSection id="key-concepts" title="Key Concepts">
              <FeatureTable rows={[
                ['Concept', 'Description'],
                ['Collection', 'A named group of papers you curate. Collections can be private or shared with a team.'],
                ['Team', 'A workspace shared by multiple users — typically a lab group. Teams enable shared collections and digests.'],
                ['Monitor', 'A saved search that runs automatically and emails you when new papers match.'],
                ['Digest', 'A weekly email summary of your team\'s activity — papers added, tasks, and member contributions.'],
                ['Trial', 'New accounts get a 14-day Pro trial with full feature access before the plan is required.'],
              ]} />
            </DocSubSection>
          </DocSection>

          {/* ── GETTING STARTED ── */}
          <DocSection id="getting-started" title="Getting Started">
            <DocSubSection id="create-account" title="Create an Account">
              <p>To get started with Mnevia, create a free account:</p>
              <StepList steps={[
                <>Go to <strong>mnevia.com</strong> and click <strong>Get Started Free</strong>.</>,
                <>Enter a username, email address, and password (minimum 6 characters).</>,
                <>Click <strong>Register</strong>. Your account is created instantly.</>,
                <>You will be logged in automatically and prompted to choose your role.</>,
              ]} />
              <Callout type="tip">
                Your 14-day Pro trial starts immediately on registration — no credit card required.
              </Callout>
            </DocSubSection>

            <DocSubSection id="choose-role" title="Choose Your Role">
              <p>
                After registering, Mnevia asks you to identify your role. This personalises the
                Quick Start checklist and recommended workflows.
              </p>
              <FeatureTable rows={[
                ['Role', 'Best for', 'Default Quick Start focus'],
                ['Principal Investigator (PI)', 'Lab directors, research leads', 'Collections → Teams → Digest → AI analysis'],
                ['Individual Contributor (IC)', 'PhD students, postdocs, researchers', 'Search → Collections → Citation tracing → Monitors'],
              ]} />
              <Callout type="note">
                You can switch between role tabs on the Quick Start page at any time. Your choice
                does not restrict any features.
              </Callout>
            </DocSubSection>

            <DocSubSection id="pro-trial" title="14-Day Pro Trial">
              <p>
                Every new account receives a <strong>14-day Pro trial</strong> automatically. During
                the trial you have full access to all Pro features:
              </p>
              <ul className="docs-list">
                <li>Unlimited searches (up to 3,000/month)</li>
                <li>Up to 100 collections with 1,000 items each</li>
                <li>Create and manage teams (up to 10 teams, 25 members/team)</li>
                <li>300,000 AI chatbot tokens per month</li>
                <li>20 GB storage and 300 PDF uploads per month</li>
                <li>AI Literature Review generator</li>
                <li>Research Monitors</li>
              </ul>
              <p>
                A countdown banner is shown at the top of every page during your trial. When the
                trial ends, your account reverts to the Free plan unless you subscribe.
              </p>
              <Callout type="info">
                To subscribe before your trial ends, go to <strong>Membership</strong> → select
                your plan → click <strong>Subscribe Now</strong>.
              </Callout>
            </DocSubSection>

          </DocSection>

          {/* ── SEARCH ── */}
          <DocSection id="search" title="Search & Discovery">
            <DocSubSection id="basic-search" title="Basic Search">
              <p>
                Mnevia searches across millions of scholarly works indexed by
                <a href="https://openalex.org" target="_blank" rel="noopener noreferrer" className="docs-link"> OpenAlex <FiExternalLink size={11} /></a>.
                Enter any keyword, phrase, author name, or topic into the search bar and press Enter.
              </p>
              <p>Results show:</p>
              <ul className="docs-list">
                <li>Title, authors, and publication year</li>
                <li>Abstract excerpt</li>
                <li>Open Access status and journal name</li>
                <li>Relevance score</li>
                <li>Citation and reference counts</li>
              </ul>
              <Callout type="tip">
                Click the bookmark icon on any result to save it directly to a collection.
              </Callout>
            </DocSubSection>

            <DocSubSection id="search-modes" title="Search Modes">
              <p>Use the search options (gear icon) to choose how your query is interpreted:</p>
              <FeatureTable rows={[
                ['Mode', 'Description', 'Example'],
                ['Boolean', 'Standard keyword matching with AND/OR/NOT support.', '"CRISPR AND gene editing NOT plants"'],
                ['Title & Abstract', 'Searches both title and abstract fields (default).', '"deep learning"'],
                ['Title only', 'Searches only the paper title. Faster for precise lookups.', '"attention is all you need"'],
                ['Full text', 'Searches full text where available.', '"hippocampal neurogenesis"'],
              ]} />
            </DocSubSection>

            <DocSubSection id="filters" title="Filters & Sorting">
              <p>Refine your results using the filter panel below the search bar:</p>
              <ul className="docs-list">
                <li><strong>Publication year</strong> — restrict to a specific year or range.</li>
                <li><strong>Open Access</strong> — show only freely available papers.</li>
                <li><strong>Document type</strong> — journal articles, preprints, book chapters, etc.</li>
                <li><strong>Author</strong> — filter by a specific author's OpenAlex ID.</li>
                <li><strong>Topics</strong> — filter by research topic/field.</li>
                <li><strong>Journal</strong> — include or exclude specific journals.</li>
                <li><strong>Institution</strong> — filter by author affiliation.</li>
                <li><strong>Relevance threshold</strong> — hide results below a score cutoff.</li>
              </ul>
              <p>Sort results by <strong>Relevance</strong>, <strong>Publication date</strong>, or <strong>Citation count</strong> using the sort dropdown.</p>
              <Callout type="tip">
                Click <strong>Save This Search</strong> (🔔) to turn any search into a Monitor and receive email alerts for new matching papers.
              </Callout>
            </DocSubSection>

            <DocSubSection id="citation-tracing" title="Citation Tracing">
              <p>
                Every search result has three citation buttons for bidirectional network exploration:
              </p>
              <FeatureTable rows={[
                ['Button', 'Shows'],
                ['Cited by', 'Papers that cite this work — see who builds on it.'],
                ['Cites', 'Papers this work references — trace its intellectual lineage.'],
                ['Related', 'Semantically similar papers based on OpenAlex\'s topic model.'],
              ]} />
              <p>
                Citation search results are paginated and sortable. Use the <strong>Back to
                Previous Search</strong> button to return to your original results.
              </p>
            </DocSubSection>

            <DocSubSection id="research-monitors" title="Research Monitors">
              <p>See the <a href="#monitor-setup" className="docs-link" onClick={() => scrollTo('monitor-setup')}>Research Monitors</a> section for full details.</p>
            </DocSubSection>
          </DocSection>

          {/* ── COLLECTIONS ── */}
          <DocSection id="collections" title="Collections">
            <DocSubSection id="create-collection" title="Create a Collection">
              <p>Collections are the primary way to organise papers in Mnevia.</p>
              <StepList steps={[
                <>Go to <strong>Collections</strong> from the header.</>,
                <>Click <strong>New Collection</strong>.</>,
                <>Enter a name and optional description.</>,
                <>Click <strong>Create</strong>. The collection is created and opens immediately.</>,
              ]} />
              <Callout type="note">
                Free plan accounts can create up to <strong>3 collections</strong>. Pro accounts get up to 100.
              </Callout>
            </DocSubSection>

            <DocSubSection id="save-papers" title="Save Papers to a Collection">
              <p>There are two ways to save a paper:</p>
              <ul className="docs-list">
                <li><strong>From search results</strong> — click the bookmark icon (🔖) on any result and choose a collection from the dropdown.</li>
                <li><strong>From inside a collection</strong> — use the search bar within the collection view to find and add papers directly.</li>
              </ul>
              <p>Inside a collection you can also:</p>
              <ul className="docs-list">
                <li>Add notes to any paper.</li>
                <li>Create action items / tasks linked to a paper.</li>
                <li>Remove papers you no longer need.</li>
              </ul>
            </DocSubSection>

            <DocSubSection id="upload-pdf" title="Upload PDFs">
              <p>
                Attach a PDF to any paper in your collection. Mnevia stores it securely and makes
                it available for AI analysis and chat.
              </p>
              <StepList steps={[
                <>Open a collection and find the paper you want to attach a PDF to.</>,
                <>Click the <strong>Upload PDF</strong> button next to the paper.</>,
                <>Select your PDF file (max 50 MB per file).</>,
                <>The PDF is processed and attached. A 📎 icon confirms the attachment.</>,
              ]} />
              <Callout type="note">
                Free plan: 5 PDF uploads/month. Pro plan: 300 uploads/month.
              </Callout>
            </DocSubSection>

            <DocSubSection id="share-collection" title="Share with a Team">
              <p>
                Sharing a collection with a team lets all team members view and contribute to it.
              </p>
              <StepList steps={[
                <>Open the collection you want to share.</>,
                <>Click the <strong>Share</strong> button in the collection header.</>,
                <>Select the team to share with from the dropdown.</>,
                <>Click <strong>Confirm</strong>. Team members can now see and add papers to the collection.</>,
              ]} />
              <Callout type="info">
                Only team owners can manage shared collections. Members can add papers and notes
                but cannot delete the collection.
              </Callout>
            </DocSubSection>

            <DocSubSection id="ai-chat" title="AI Chat">
              <p>
                Start a conversation with your collection using the built-in AI assistant.
                The AI reads the abstracts and titles of all papers in the collection and answers
                questions, generates summaries, and suggests research directions.
              </p>
              <StepList steps={[
                <>Open any collection.</>,
                <>Click <strong>AI Chat</strong> in the collection toolbar.</>,
                <>Type a question or prompt in the chat input and press Enter.</>,
                <>The AI responds based on the papers in your collection.</>,
              ]} />
              <p><strong>Example prompts:</strong></p>
              <ul className="docs-list">
                <li>"Summarise the main findings across these papers."</li>
                <li>"What are the open research questions in this field?"</li>
                <li>"Which papers would be most relevant for a methods section?"</li>
                <li>"Are there any contradictory findings between these papers?"</li>
              </ul>
              <Callout type="note">
                AI chat uses tokens from your monthly allowance. Free plan: 20,000 tokens/month. Pro: 300,000 tokens/month.
              </Callout>
            </DocSubSection>

            <DocSubSection id="lit-review" title="Literature Review Generator">
              <p>
                Generate a structured, citation-formatted literature review from your collection in seconds.
              </p>
              <StepList steps={[
                <>Open a collection (works best with 5–30 papers).</>,
                <>Click <strong>Lit Review</strong> in the collection toolbar.</>,
                <>Enter a focus or angle (e.g., "methods for measuring neuroplasticity").</>,
                <>Choose a target length: Short (~500 words), Medium (~900), or Long (~1,400).</>,
                <>Click <strong>Generate</strong>. The review is ready in under 30 seconds.</>,
                <>Copy to clipboard or download as a <code>.md</code> Markdown file.</>,
              ]} />
              <p>The generated review includes:</p>
              <ul className="docs-list">
                <li>Introduction, Key Themes, Methods & Approaches, Research Gaps, Conclusions</li>
                <li>Inline citations in (Author, Year) format</li>
                <li>A References section with all cited works</li>
              </ul>
              <Callout type="warning">
                Literature reviews are AI-generated and should be reviewed for accuracy before use in publications. Always verify citations against the original papers.
              </Callout>
            </DocSubSection>
          </DocSection>

          {/* ── TEAMS ── */}
          <DocSection id="teams" title="Teams">
            <DocSubSection id="create-team" title="Create a Team">
              <p>
                Teams provide a shared workspace for your lab or research group. The user who
                creates the team becomes its <strong>owner</strong>.
              </p>
              <StepList steps={[
                <>Go to <strong>Teams</strong> from the header.</>,
                <>Click <strong>Create Team</strong>.</>,
                <>Enter a team name and optional description.</>,
                <>Click <strong>Create Team</strong>. The team is created and selected immediately.</>,
              ]} />
              <Callout type="note">
                Team creation requires a <strong>Pro plan</strong>. Free plan users can join teams
                but cannot create them. Upgrade from the <strong>Membership</strong> page.
              </Callout>
            </DocSubSection>

            <DocSubSection id="invite-members" title="Invite Members">
              <p>Invite colleagues to your team by email:</p>
              <StepList steps={[
                <>Select your team from the Teams list.</>,
                <>Click <strong>Invite Member</strong> in the Members section.</>,
                <>Enter the email address of the person to invite.</>,
                <>Click <strong>Invite</strong>. They will be added to the team.</>,
              ]} />
              <Callout type="info">
                The invited user must already have a Mnevia account. Invitations match by email address.
              </Callout>
            </DocSubSection>

            <DocSubSection id="team-roles" title="Roles & Permissions">
              <FeatureTable rows={[
                ['Role', 'Can invite members', 'Can remove members', 'Can delete team', 'Can manage digest', 'Can view team'],
                ['Owner', '✅', '✅', '✅', '✅', '✅'],
                ['Member', '❌', '❌', '❌', '❌', '✅'],
              ]} />
              <p>Each team has exactly one owner. Ownership cannot be transferred in the current version.</p>
            </DocSubSection>

            <DocSubSection id="weekly-digest" title="Weekly Digest">
              <p>
                The weekly digest is an automated email summary of your team's activity,
                sent every week on a day you choose.
              </p>
              <p>The digest includes:</p>
              <ul className="docs-list">
                <li>Papers added to team collections during the period</li>
                <li>Collection activity per member</li>
                <li>AI chat sessions</li>
                <li>Task/action item status (created, completed, overdue)</li>
                <li>Inactive member alerts</li>
                <li>Research monitor alerts for team members</li>
              </ul>
              <StepList steps={[
                <>Select your team and scroll to the <strong>Weekly Digest</strong> section (owners only).</>,
                <>Toggle the digest <strong>Enabled</strong>.</>,
                <>Choose the send day (Monday–Sunday) and recipients (owners only, all members, or custom).</>,
                <>Click <strong>Preview Digest</strong> to see what the next email will contain.</>,
                <>Click <strong>Send Now</strong> to dispatch the digest immediately for testing.</>,
              ]} />
            </DocSubSection>
          </DocSection>

          {/* ── MONITORS ── */}
          <DocSection id="monitors" title="Research Monitors">
            <DocSubSection id="monitor-setup" title="Set Up a Monitor">
              <p>
                Research Monitors save a search query and automatically check for new matching
                papers. When new results are found, Mnevia sends you an email alert.
              </p>
              <p><strong>Method 1 — From a search result:</strong></p>
              <StepList steps={[
                <>Run a search in Mnevia.</>,
                <>Click <strong>Save This Search</strong> (🔔) above the results.</>,
                <>You are taken to the Monitors page with your query pre-filled.</>,
                <>Set a name, choose the check frequency (daily / weekly), and save.</>,
              ]} />
              <p><strong>Method 2 — From the Monitors page:</strong></p>
              <StepList steps={[
                <>Go to <strong>Monitors</strong> from the header.</>,
                <>Click <strong>New Monitor</strong>.</>,
                <>Enter a search query, optional filters, a name, and check frequency.</>,
                <>Click <strong>Create Monitor</strong>.</>,
              ]} />
            </DocSubSection>

            <DocSubSection id="monitor-alerts" title="Email Alerts">
              <p>
                When a monitor finds new papers since its last check, you receive an email listing
                each new paper with its title, authors, year, and a direct link.
              </p>
              <p>
                Monitor checks run automatically. You can also trigger a manual check from the
                Monitors page by clicking <strong>Check Now</strong>.
              </p>
              <Callout type="tip">
                To pause a monitor without deleting it, use the toggle on the Monitors page. Paused
                monitors do not send alerts and do not consume checks.
              </Callout>
            </DocSubSection>
          </DocSection>

          {/* ── BILLING ── */}
          <DocSection id="billing" title="Plans & Billing">
            <DocSubSection id="plans-overview" title="Plans Overview">
              <FeatureTable rows={[
                ['Feature', 'Free', 'Pro', 'Enterprise'],
                ['Searches / month', '250', '3,000', 'Unlimited'],
                ['Collections', '3', '100', 'Unlimited'],
                ['Items per collection', '100', '1,000', 'Unlimited'],
                ['AI tokens / month', '20,000', '300,000', '5,000,000'],
                ['Storage', '1 GB', '20 GB', '200 GB'],
                ['PDF uploads / month', '5', '300', 'Unlimited'],
                ['Teams (create)', '0', '10', 'Unlimited'],
                ['Team memberships', '1', '20', 'Unlimited'],
                ['Members per team', '5', '25', 'Unlimited'],
                ['Price (monthly)', 'Free', '$30 / month', '$50 / seat / month'],
                ['Price (yearly)', 'Free', '$360 / year', '$600 / seat / year'],
              ]} />
              <Callout type="info">
                Free users can <strong>join</strong> one team (invited by a Pro user) even though they cannot create teams.
              </Callout>
            </DocSubSection>

            <DocSubSection id="upgrade" title="Upgrade or Switch Plans">
              <StepList steps={[
                <>Go to <strong>Membership</strong> from the header.</>,
                <>Scroll to the <strong>Change Plan</strong> section (or <strong>Activate Your Subscription</strong> during a trial).</>,
                <>Toggle between <strong>Monthly</strong> and <strong>Yearly</strong> billing.</>,
                <>Click the button on the plan you want (<strong>Subscribe Now</strong>, <strong>Upgrade to Pro</strong>, or <strong>Switch to Enterprise</strong>).</>,
                <>Complete checkout via Stripe. You are redirected back to Mnevia once payment is confirmed.</>,
              ]} />
              <p>To <strong>cancel</strong>, click <strong>Cancel Subscription</strong> in the Current Plan card. Access continues until the end of your billing period.</p>
            </DocSubSection>

            <DocSubSection id="enterprise" title="Enterprise">
              <p>
                Enterprise is a per-seat plan designed for labs and institutions that need centralised
                billing, higher limits, and multiple teams under one account.
              </p>
              <p>The Enterprise account owner manages:</p>
              <ul className="docs-list">
                <li>Purchasing seats (set member count at checkout)</li>
                <li>Adding and removing members from the enterprise account</li>
                <li>Billing and payment history</li>
              </ul>
              <p>
                Enterprise members automatically receive enterprise-tier limits regardless of their
                individual subscription status.
              </p>
              <Callout type="tip">
                To subscribe to Enterprise, go to <strong>Membership</strong> → <strong>Switch to Enterprise</strong>. You will be prompted to enter your initial seat count.
              </Callout>
            </DocSubSection>
          </DocSection>

        </div>
      </main>
    </div>
  );
};

export default Docs;
